'use client'

import type { CSSProperties, ReactNode } from 'react'
import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import {
  IconBook2,
  IconCards,
  IconChevronDown,
  IconCreditCard,
  IconDatabase,
  IconHome,
  IconLanguage,
  IconLogout,
  IconMessageCircle,
  IconArticle,
  IconSettings,
  IconSparkles,
  IconTextGrammar,
  IconUser,
  IconWifi
} from '@tabler/icons-react'
import { FeedbackDialog } from '@/components/feedback/feedback-dialog'
import { LanguageSwitcher } from '@/components/language-switcher'
import { ThemeSwitcher } from '@/components/theme-switcher'
import { LoginDialog } from '@/components/auth/login-dialog'
import { UpgradeIntentDialog } from '@/components/paywall/upgrade-intent-dialog'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useOffline } from '@/components/offline-context'
import Logo from '@/components/logo'
import { useAuth } from '@/hooks/useAuth'
import { useEntitlements } from '@/hooks/useEntitlements'
import { Link, usePathname } from '@/i18n/routing'
import { createCustomerPortalSession } from '@/lib/billing'
import { useTranslations } from 'next-intl'
import {
  canonicalRoutes,
  isActiveProductRoute,
  isImmersiveReaderRoute,
  productNavItems
} from '@/lib/product-routes'
import { cn } from '@/lib/utils'
import { analytics } from '@/lib/analytics'

const navIcons = {
  today: IconHome,
  read: IconBook2,
  dict: IconLanguage,
  grammar: IconTextGrammar,
  collections: IconCards
}

type ProductShellProps = {
  children: ReactNode
  variant?: 'standard' | 'reference' | 'workspace' | 'immersive'
  immersive?: boolean
  pageTransition?: boolean
  className?: string
}

export function ProductShell({
  children,
  variant = 'standard',
  immersive,
  pageTransition = true,
  className
}: ProductShellProps) {
  const pathname = usePathname()
  const isImmersive =
    variant === 'immersive' || (immersive ?? isImmersiveReaderRoute(pathname))
  const style = {
    '--product-shell-header-height': isImmersive ? '0px' : '64px'
  } as CSSProperties

  return (
    <div
      className={cn(
        'bg-background text-foreground min-h-dvh min-w-0',
        variant === 'reference' && 'bg-background',
        className
      )}
      data-product-shell={variant}
      style={style}
    >
      {!isImmersive && <ProductHeader pathname={pathname} />}
      <main
        className={cn(
          'min-w-0',
          !isImmersive &&
            'pb-[calc(5.75rem+env(safe-area-inset-bottom))] md:pb-0'
        )}
      >
        {isImmersive ? (
          children
        ) : pageTransition ? (
          <div className="page-fade-shell min-h-[calc(100dvh-var(--product-shell-header-height))]">
            <div key={pathname} className="page-fade-panel min-h-full">
              {children}
            </div>
          </div>
        ) : (
          <div className="min-h-[calc(100dvh-var(--product-shell-header-height))]">
            {children}
          </div>
        )}
      </main>
      {!isImmersive && <ProductMobileDock pathname={pathname} />}
    </div>
  )
}

function ProductHeader({ pathname }: { pathname: string }) {
  const tNav = useTranslations('nav')
  const [hoveredNav, setHoveredNav] = useState<string | null>(null)

  return (
    <header className="bg-background/88 supports-[backdrop-filter]:bg-background/70 sticky top-0 z-40 h-16 backdrop-blur-xl">
      <div className="mx-auto flex h-full w-full max-w-7xl items-center gap-4 px-4 md:px-6">
        <Link
          href="/"
          className="flex shrink-0 items-center gap-2 transition-opacity hover:opacity-80"
          aria-label="Miaozi homepage"
        >
          <Logo
            className="h-11 w-11 text-amber-950 dark:text-amber-100"
            aria-hidden="true"
          />
          <span className="hidden text-base font-semibold tracking-tight sm:inline">
            Miaozi
          </span>
        </Link>

        <nav
          className="relative hidden items-center gap-1 md:flex"
          onMouseLeave={() => setHoveredNav(null)}
        >
          {productNavItems.map((item) => {
            const Icon = navIcons[item.key]
            const isActive = isActiveProductRoute(pathname, item.href)
            const showPill =
              hoveredNav === item.key || (!hoveredNav && isActive)

            return (
              <Link
                key={item.href}
                href={item.href}
                onMouseEnter={() => setHoveredNav(item.key)}
                className={cn(
                  'relative isolate flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors duration-200',
                  isActive
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <AnimatePresence>
                  {showPill && (
                    <motion.span
                      layoutId="product-nav-pill"
                      className={cn(
                        'ring-border/35 absolute inset-0 -z-10 rounded-full shadow-sm ring-1',
                        isActive && !hoveredNav ? 'bg-muted/70' : 'bg-muted/55'
                      )}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 420,
                        damping: 34,
                        mass: 0.7
                      }}
                    />
                  )}
                </AnimatePresence>
                <Icon className="h-4 w-4" strokeWidth={isActive ? 2 : 1.6} />
                {getNavLabel(item.key, tNav)}
              </Link>
            )
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <Link
            href={canonicalRoutes.blog}
            className="text-muted-foreground hover:text-foreground hidden rounded-full px-3 py-2 text-sm font-semibold transition-colors lg:inline-flex"
          >
            {tNav('blog')}
          </Link>
          <div className="hidden items-center gap-1 md:flex">
            <ThemeSwitcher />
            <LanguageSwitcher />
          </div>
          <ProductAccountMenu />
        </div>
      </div>
    </header>
  )
}

function ProductMobileDock({ pathname }: { pathname: string }) {
  const tNav = useTranslations('nav')
  const navCount = productNavItems.length
  const currentIndex = Math.max(
    0,
    productNavItems.findIndex((item) =>
      isActiveProductRoute(pathname, item.href)
    )
  )
  const [pressedIndex, setPressedIndex] = useState<number | null>(null)
  const activeIndex = pressedIndex ?? currentIndex

  return (
    <nav
      className="fixed right-4 bottom-4 left-4 z-50 md:hidden"
      style={{
        paddingBottom: 'max(0px, calc(env(safe-area-inset-bottom) - 12px))'
      }}
      aria-label="Primary"
    >
      <div
        className="border-border/60 bg-background/92 relative mx-auto grid max-w-[23rem] overflow-hidden rounded-full border p-1 shadow-2xl shadow-black/15 backdrop-blur-xl"
        style={{ gridTemplateColumns: `repeat(${navCount}, minmax(0, 1fr))` }}
      >
        <span
          aria-hidden="true"
          className="bg-muted pointer-events-none absolute top-1 bottom-1 left-1 rounded-full shadow-sm transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{
            width: `calc((100% - 0.5rem) / ${navCount})`,
            transform: `translateX(${activeIndex * 100}%)`
          }}
        />
        {productNavItems.map((item, index) => {
          const Icon = navIcons[item.key]
          const isActive = isActiveProductRoute(pathname, item.href)

          return (
            <Link
              key={item.href}
              href={item.href}
              onPointerDown={() => setPressedIndex(index)}
              onPointerCancel={() => setPressedIndex(null)}
              onClick={() => {
                window.setTimeout(() => setPressedIndex(null), 450)
              }}
              onBlur={() => setPressedIndex(null)}
              className={cn(
                'relative z-10 flex min-h-12 flex-col items-center justify-center gap-0.5 rounded-full px-2 py-1 text-[11px] font-medium transition-all duration-200 active:scale-95',
                isActive
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground active:bg-muted'
              )}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className="h-5 w-5" strokeWidth={isActive ? 2 : 1.6} />
              <span className="max-w-full truncate">
                {getNavLabel(item.key, tNav)}
              </span>
            </Link>
          )
        })}
      </div>
    </nav>
  )
}

function ProductAccountMenu() {
  const { user, isLoading, signOut } = useAuth()
  const tNav = useTranslations('nav')
  const {
    isPremium,
    paywallsEnabled,
    dodoBillingEnabled,
    entitlementSource,
    billingStatus,
    refreshEntitlements
  } = useEntitlements()
  const { isOfflineEnabled, isReady, progress, statusText, toggleOffline } =
    useOffline()
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [isOpeningPortal, setIsOpeningPortal] = useState(false)

  if (isLoading) {
    return <div className="bg-muted h-9 w-9 animate-pulse rounded-full" />
  }

  if (!user) {
    return (
      <LoginDialog>
        <Button size="sm" className="rounded-full">
          <IconUser className="h-4 w-4" />
          <span className="hidden sm:inline">Sign in</span>
        </Button>
      </LoginDialog>
    )
  }

  const displayName =
    (user.user_metadata?.full_name as string | undefined) ||
    user.email ||
    'Account'
  const planName = paywallsEnabled
    ? isPremium
      ? 'Pro'
      : 'Free'
    : 'Free access'
  const isOfflineReady = isOfflineEnabled && isReady
  const isDownloading = progress !== null
  const canManageSubscription =
    dodoBillingEnabled &&
    billingStatus !== 'none' &&
    billingStatus !== 'unknown'

  const handleOpenPortal = async () => {
    setIsOpeningPortal(true)
    try {
      const result = await createCustomerPortalSession()
      analytics.portalOpened('product-shell', {
        entitlement_source: entitlementSource,
        billing_status: billingStatus
      })
      window.location.href = result.link
    } catch (error) {
      console.error('Failed to open customer portal', error)
    } finally {
      setIsOpeningPortal(false)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-9 rounded-full px-1.5 pr-2"
            aria-label="Account menu"
          >
            <span className="bg-primary text-primary-foreground flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold">
              {getInitials(displayName)}
            </span>
            <IconChevronDown className="text-muted-foreground h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72 p-2">
          <DropdownMenuLabel className="px-2 py-2">
            <span className="block truncate text-sm font-semibold">
              {displayName}
            </span>
            {user.email && (
              <span className="text-muted-foreground block truncate text-xs font-normal">
                {user.email}
              </span>
            )}
          </DropdownMenuLabel>

          <div className="bg-muted/45 mx-1 mb-1 rounded-lg p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold">
                  {planName}
                  {paywallsEnabled ? ' plan' : ''}
                </p>
                <p className="text-muted-foreground truncate text-xs">
                  {!paywallsEnabled
                    ? 'All stories and tools are available'
                    : isPremium
                      ? 'Pro stories unlocked'
                      : 'Tools are free. Pro unlocks selected stories.'}
                </p>
              </div>
              {!paywallsEnabled ? null : isPremium ? (
                <IconSparkles className="text-primary h-4 w-4 shrink-0" />
              ) : (
                <Button
                  size="sm"
                  className="h-7 rounded-full px-3 text-xs"
                  onClick={() => {
                    analytics.paywallCtaClicked(
                      'premium-chapters',
                      'product-shell',
                      { trigger: 'account-menu' }
                    )
                    setUpgradeOpen(true)
                  }}
                >
                  Upgrade
                </Button>
              )}
            </div>
          </div>

          <div className="mx-1 mb-1 flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
            <div className="flex min-w-0 items-center gap-2">
              {isDownloading ? (
                <IconDatabase className="text-primary h-4 w-4 shrink-0 animate-pulse" />
              ) : isOfflineReady ? (
                <IconDatabase className="h-4 w-4 shrink-0 text-emerald-500" />
              ) : (
                <IconWifi className="h-4 w-4 shrink-0 text-blue-500" />
              )}
              <div className="min-w-0">
                <p className="text-xs font-medium">
                  {isDownloading
                    ? `${Math.round(progress ?? 0)}% downloaded`
                    : isOfflineReady
                      ? 'Offline dictionary'
                      : 'Online dictionary'}
                </p>
                <p className="text-muted-foreground truncate text-[11px]">
                  {statusText}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => {
                toggleOffline()
              }}
              disabled={isDownloading}
            >
              {isOfflineEnabled ? 'Off' : 'On'}
            </Button>
          </div>

          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link href={canonicalRoutes.settings}>
              <IconSettings className="h-4 w-4" />
              Settings
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href={canonicalRoutes.blog}>
              <IconArticle className="h-4 w-4" />
              {tNav('blog')}
            </Link>
          </DropdownMenuItem>
          <FeedbackDialog>
            <button className="focus:bg-accent focus:text-accent-foreground flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden">
              <IconMessageCircle className="text-muted-foreground h-4 w-4" />
              Feedback
            </button>
          </FeedbackDialog>
          {canManageSubscription && (
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault()
                void handleOpenPortal()
              }}
              disabled={isOpeningPortal}
            >
              <IconCreditCard className="h-4 w-4" />
              {isOpeningPortal ? 'Opening portal...' : 'Manage plan'}
            </DropdownMenuItem>
          )}
          {dodoBillingEnabled && (
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault()
                void refreshEntitlements()
              }}
            >
              <IconSparkles className="h-4 w-4" />
              Refresh plan
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onSelect={() => void signOut()}
          >
            <IconLogout className="h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {paywallsEnabled && (
        <UpgradeIntentDialog
          open={upgradeOpen}
          onOpenChange={setUpgradeOpen}
          paywallType="premium-chapters"
          surface="product-shell"
          title="Unlock Pro stories"
        />
      )}
    </>
  )
}

function getInitials(value: string) {
  const parts = value
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)

  return parts.map((part) => part.charAt(0).toUpperCase()).join('') || 'U'
}

function getNavLabel(
  key: (typeof productNavItems)[number]['key'],
  tNav: (
    key: 'today' | 'dictionary' | 'reader' | 'grammar' | 'collections'
  ) => string
) {
  if (key === 'today') return 'Home'
  if (key === 'read') return tNav('reader')
  if (key === 'dict') return tNav('dictionary')
  if (key === 'grammar') return tNav('grammar')
  return tNav('collections')
}
